import { useState, useEffect, useRef } from 'react';

const GEOCODE_STORAGE_PREFIX = 'geocode:';
const PLACEHOLDER = 'Address unavailable';

let requestQueue = [];
let isProcessingQueue = false;
let queuePaused = false;
let activeConsumerCount = 0;

const memoryCache = new Map();

function storageKey(lat, lng) {
  return `${GEOCODE_STORAGE_PREFIX}${lat},${lng}`;
}

function readFromStorage(lat, lng) {
  const key = storageKey(lat, lng);
  if (memoryCache.has(key)) return memoryCache.get(key);
  try {
    const stored = localStorage.getItem(key);
    if (stored) {
      memoryCache.set(key, stored);
      return stored;
    }
  } catch {
    /* ignore */
  }
  return null;
}

function writeToStorage(lat, lng, address) {
  const key = storageKey(lat, lng);
  memoryCache.set(key, address);
  try {
    localStorage.setItem(key, address);
  } catch {
    /* ignore quota */
  }
}

export function registerGeocodeConsumer() {
  activeConsumerCount += 1;
  queuePaused = false;
  processQueue();
  return () => {
    activeConsumerCount = Math.max(0, activeConsumerCount - 1);
    if (activeConsumerCount === 0) {
      queuePaused = true;
    }
  };
}

const getStateAbbreviation = (stateName) => {
  const stateMap = {
    Alabama: 'AL', Alaska: 'AK', Arizona: 'AZ', Arkansas: 'AR',
    California: 'CA', Colorado: 'CO', Connecticut: 'CT', Delaware: 'DE',
    Florida: 'FL', Georgia: 'GA', Hawaii: 'HI', Idaho: 'ID',
    Illinois: 'IL', Indiana: 'IN', Iowa: 'IA', Kansas: 'KS',
    Kentucky: 'KY', Louisiana: 'LA', Maine: 'ME', Maryland: 'MD',
    Massachusetts: 'MA', Michigan: 'MI', Minnesota: 'MN', Mississippi: 'MS',
    Missouri: 'MO', Montana: 'MT', Nebraska: 'NE', Nevada: 'NV',
    'New Hampshire': 'NH', 'New Jersey': 'NJ', 'New Mexico': 'NM', 'New York': 'NY',
    'North Carolina': 'NC', 'North Dakota': 'ND', Ohio: 'OH', Oklahoma: 'OK',
    Oregon: 'OR', Pennsylvania: 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
    'South Dakota': 'SD', Tennessee: 'TN', Texas: 'TX', Utah: 'UT',
    Vermont: 'VT', Virginia: 'VA', Washington: 'WA', 'West Virginia': 'WV',
    Wisconsin: 'WI', Wyoming: 'WY', 'District of Columbia': 'DC',
  };
  return stateMap[stateName] || stateName;
};

const processQueue = async () => {
  if (isProcessingQueue || queuePaused || requestQueue.length === 0) return;

  isProcessingQueue = true;

  while (requestQueue.length > 0 && !queuePaused) {
    const { lat, lng, resolve, reject } = requestQueue.shift();

    const cached = readFromStorage(lat, lng);
    if (cached) {
      resolve(cached);
      continue;
    }

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`,
        {
          headers: {
            'User-Agent': 'AutonomousDispatcher/1.0 (contact@example.com)',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      let shortAddress = '';
      if (data.address) {
        const parts = [];
        if (data.address.road || data.address.street) {
          parts.push(
            data.address.house_number
              ? `${data.address.house_number} ${data.address.road || data.address.street}`
              : data.address.road || data.address.street
          );
        }
        if (data.address.city || data.address.town || data.address.village) {
          parts.push(data.address.city || data.address.town || data.address.village);
        }
        if (data.address.state) {
          parts.push(getStateAbbreviation(data.address.state));
        }
        if (data.address.postcode) {
          parts.push(data.address.postcode);
        }
        shortAddress = parts.join(', ');
      }

      const address = shortAddress || data.display_name || PLACEHOLDER;
      writeToStorage(lat, lng, address);
      resolve(address);
    } catch (error) {
      console.error('Geocoding error:', error);
      reject(error);
    }

    if (requestQueue.length > 0 && !queuePaused) {
      await new Promise((r) => setTimeout(r, 1100));
    }
  }

  isProcessingQueue = false;
};

/**
 * Reverse geocode hook — never blocks card render.
 * Shows PLACEHOLDER immediately; resolves from localStorage or background queue.
 */
const useReverseGeocode = (lat, lng) => {
  const [address, setAddress] = useState(PLACEHOLDER);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    const unregister = registerGeocodeConsumer();
    return () => {
      mountedRef.current = false;
      unregister();
    };
  }, []);

  useEffect(() => {
    setError(null);

    if (lat == null || lng == null || lat === '' || lng === '') {
      setAddress(PLACEHOLDER);
      setLoading(false);
      return undefined;
    }

    const cached = readFromStorage(lat, lng);
    if (cached) {
      setAddress(cached);
      setLoading(false);
      return undefined;
    }

    setAddress(PLACEHOLDER);
    setLoading(true);

    let cancelled = false;

    const runAfterPaint = window.requestAnimationFrame(() => {
      window.setTimeout(() => {
        if (cancelled) return;

        const promise = new Promise((resolve, reject) => {
          requestQueue.push({ lat, lng, resolve, reject });
        });

        processQueue();

        promise
          .then((result) => {
            if (mountedRef.current && !cancelled) {
              setAddress(result);
              setLoading(false);
            }
          })
          .catch((err) => {
            if (mountedRef.current && !cancelled) {
              setError(err.message || 'Failed to geocode');
              setLoading(false);
            }
          });
      }, 0);
    });

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(runAfterPaint);
    };
  }, [lat, lng]);

  return { address, loading, error };
};

export default useReverseGeocode;
